import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { Configuration, GlobalImageLibraryApi, ImageFileResponseDto } from "../api/client-axios";
import { ApiError } from "../model/apiError";
import { Problem } from "../model/problem";
import { apiConfiguration } from "./server";
import { applyRejection } from "./thunkError";


export type ImageResult ={
    image: ImageFileResponseDto, url: string
}

export interface ImagesLibraryState {
    images?: ImageResult[],
    selectedImage?: ImageFileResponseDto,
    status: "idle" | "loading" | "error" | "success" | "auth-error" | "deleting";
    error?: string | Problem;
    code?: number;
    loaded: number;
    total: number;
}


const initialImagesLibraryState: ImagesLibraryState = {
    status: "idle",
    error: "",
    loaded: 0,
    total: 0
}

const readFileAsync = async (data: any) : Promise<ArrayBuffer | string | null> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(reader.result);
        }
        reader.onerror = (error) => {
            reject(error);
        }
        reader.readAsDataURL(data);
    });
}

// Fetches only the image metadata list, then kicks off an independent content
// fetch per image so they can stream in one-by-one instead of blocking on the
// whole batch. Returns the list so the reducer can set the expected total.
export const fetchImages = createAsyncThunk<ImageFileResponseDto[], void, { rejectValue: ApiError }>('imageLibrary/fetchImages', async (_, thunkApi) => {
    try {
        const api = new GlobalImageLibraryApi(apiConfiguration());
        const response = await api.apiV1AdministrationImageLibraryGet();
        const images = response.data as ImageFileResponseDto[];
        images.forEach((image) => {
            thunkApi.dispatch(fetchImageContent(image));
        });
        return images;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});

// Fetches the binary content for a single image and resolves it to a data URL.
// Each resolution appends to the store, so images appear progressively.
export const fetchImageContent = createAsyncThunk<ImageResult, ImageFileResponseDto, { rejectValue: ApiError }>('imageLibrary/fetchImageContent', async (image, thunkApi) => {
    try {
        const api = new GlobalImageLibraryApi(apiConfiguration());
        const r = await api.apiV1AdministrationImageLibraryIdContentGet(image.id!, { responseType: 'blob' });
        const url = await readFileAsync(r.data);
        return { image, url: url as string } as ImageResult;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});


// export const uploadImage = createAsyncThunk<ImageFileResponseDto[], void, { rejectValue: ApiError }>('imageLibrary/uploadImage', async (args, thunkApi) => {
//     try {
//         const api = new GlobalImageLibraryApi(apiConfiguration());
//         await api.apiV1AdministrationImageLibraryPost(args);
//         const response = await api.apiV1AdministrationImageLibraryGet();
//         return response.data;
//     } catch (error: AxiosError | any) {
//         return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
//     }
// });

export const deleteImage = createAsyncThunk<ImageFileResponseDto[],string, { rejectValue: ApiError }>('imageLibrary/deleteImage', async (imageId, thunkApi) => {
    try {
        const api = new GlobalImageLibraryApi(apiConfiguration());
        await api.apiV1AdministrationImageLibraryIdDelete(imageId);
        const response = await api.apiV1AdministrationImageLibraryGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});

export const selectImage = createAction("imageLibrary/selectImage", (image: ImageFileResponseDto) => ({ payload: image }));
export const clearErrorImages = createAction("imageLibrary/clearErrorImages");
export const startDeleteImage = createAction("imageLibrary/startDeleteImage");
export const showImageError = createAction("imageLibrary/showImageError", (error: Problem) => ({ payload: error }));

const imagesLibrarysSlice = createSlice({
    name: 'audioLibrary',
    initialState: initialImagesLibraryState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchImages.pending, (state, _) => {
                state.status = "loading";
                state.images = [];
                state.loaded = 0;
                state.total = 0;
            })
            .addCase(fetchImages.fulfilled, (state, action) => {
                state.total = action.payload.length;
                // Show the page immediately; contents stream in via fetchImageContent.
                state.status = "success";
            })
            .addCase(fetchImages.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(fetchImageContent.fulfilled, (state, action) => {
                state.images = [...(state.images ?? []), action.payload];
                state.loaded += 1;
            })
            .addCase(fetchImageContent.rejected, (state) => {
                // Count failures toward progress so the indicator can complete.
                state.loaded += 1;
            })
            .addCase(deleteImage.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(deleteImage.fulfilled, (state, action) => {
              
                state.status = "success";
            })
            .addCase(deleteImage.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(selectImage, (state, action) => {
                state.selectedImage = action.payload;
            })
            .addCase(clearErrorImages, (state) => {
                state.status = "success";
             })
             .addCase(startDeleteImage, (state) => {
                 state.status = "deleting";
             })
             .addCase(showImageError,(state, action) => {
                state.status = "error";
                state.error = action.payload;
             }) 
            ;


    }
});

export default imagesLibrarysSlice.reducer;
